// Signal-boundary and overlap validation, built on the same bit-mapping
// algorithm the grid/decoder use — so "does this signal fit" and "which
// bits does it highlight" can never disagree.
import { getSignalBitPositions, cellKey } from './bitMapping';

export function signalBitCells(signal) {
  return getSignalBitPositions(signal.startBit, signal.length, signal.byteOrder)
    .map((p) => cellKey(p.byteIndex, p.bitInByte));
}

export function fitsWithinDlc(signal, dlc) {
  if (!Number.isInteger(signal.startBit) || !Number.isInteger(signal.length)) return false;
  if (signal.startBit < 0 || signal.length < 1 || signal.length > 64) return false;
  const positions = getSignalBitPositions(signal.startBit, signal.length, signal.byteOrder);
  return positions.every((p) => p.byteIndex >= 0 && p.byteIndex < dlc);
}

// Every signal id that shares at least one physical bit with another signal.
export function findOverlappingSignalIds(signals) {
  const ownerByCell = new Map();
  const overlapping = new Set();
  for (const signal of signals) {
    for (const key of signalBitCells(signal)) {
      const existingOwner = ownerByCell.get(key);
      if (existingOwner && existingOwner !== signal.id) {
        overlapping.add(signal.id);
        overlapping.add(existingOwner);
      } else {
        ownerByCell.set(key, signal.id);
      }
    }
  }
  return overlapping;
}

// Field-level validation for the add/edit signal form. `otherSignals` should
// exclude the signal being edited (or include it — it's filtered by id).
export function validateSignalDraft(signal, dlc, otherSignals) {
  const errors = {};

  if (!signal.name || !signal.name.trim()) {
    errors.name = 'Name is required.';
  } else if (otherSignals.some((s) => s.id !== signal.id && s.name.trim() === signal.name.trim())) {
    errors.name = 'Name must be unique among this frame’s signals.';
  }

  if (!Number.isInteger(signal.startBit) || signal.startBit < 0 || signal.startBit > 63) {
    errors.startBit = 'Must be a whole number, 0–63.';
  }

  if (!Number.isInteger(signal.length) || signal.length < 1 || signal.length > 64) {
    errors.length = 'Must be a whole number, 1–64.';
  }

  if (!errors.startBit && !errors.length && !fitsWithinDlc(signal, dlc)) {
    errors.bounds = `Signal extends beyond the selected DLC (${dlc} byte${dlc === 1 ? '' : 's'}).`;
  }

  if (!errors.startBit && !errors.length && !errors.bounds) {
    const cells = new Set(signalBitCells(signal));
    const overlapsWith = otherSignals.filter(
      (s) => s.id !== signal.id && signalBitCells(s).some((k) => cells.has(k))
    );
    if (overlapsWith.length) {
      errors.overlap = `Overlaps ${overlapsWith.map((s) => s.name || 'Unnamed').join(', ')}.`;
    }
  }

  if (!Number.isFinite(signal.factor)) errors.factor = 'Must be a number.';
  if (!Number.isFinite(signal.offset)) errors.offset = 'Must be a number.';

  if (!Number.isFinite(signal.physicalMin)) errors.physicalMin = 'Must be a number.';
  if (!Number.isFinite(signal.physicalMax)) errors.physicalMax = 'Must be a number.';
  if (!errors.physicalMin && !errors.physicalMax && signal.physicalMin > signal.physicalMax) {
    errors.physicalMax = 'Physical Max must be ≥ Physical Min.';
  }

  return errors;
}

export function hasErrors(errors) {
  return Object.keys(errors).length > 0;
}
